import { Injectable, signal, effect } from '@angular/core';
import { AuthRepository } from '../../../domain/repositories/auth.repository';
import { StorageAdapter } from '../../../domain/adapters/storage.adapter';
import { lastValueFrom, tap } from 'rxjs';
import { Result } from '../../../domain/models/http-response.model';
import {
  HttpClientAdapter,
  HttpClientEvent,
} from '../../../domain/adapters/http.adapter';
import { RegisterUserDto } from '../../../domain/dtos/auth.dto';
import {
  DeleteUserResponseModel,
  PublicUserModel,
  UserModel,
} from '../../../domain/models/user-profile.dto';
import { ChangePasswordDto } from '../../../domain/dtos/change-password.dto';
import { ResetPasswordDto } from '../../../domain/dtos/reset-password.dto';
import { objectToFormData } from '../../../lib/object-to-form-data';
import { UpdateUserDto } from '../../../domain/dtos/update-user.dto';

@Injectable()
export class AuthRepositoryImpl implements AuthRepository {
  private _isLoggedIn = signal<boolean>(
    this.storage.getBlocking('authToken') !== null
  );
  isLoggedIn = this._isLoggedIn.asReadonly();

  private _userProfile = signal<UserModel | null>(null);
  userProfile = this._userProfile.asReadonly();

  init = effect(() => {
    if (this._isLoggedIn()) {
      this.getProfile();
    }
  });

  constructor(
    private storage: StorageAdapter,
    private httpClient: HttpClientAdapter
  ) {
    this.httpClient.event$.subscribe((event) => {
      if (event === HttpClientEvent.UNAUTHORIZED) {
        this.clearAuthToken();
      }
    });
  }

  private clearAuthToken() {
    this.storage.remove('authToken');
    this._isLoggedIn.set(false);
  }
  private clearUserProfile() {
    this._userProfile.set(null);
  }

  async login(
    email: string,
    password: string
  ): Promise<Result<{ token: string }, any>> {
    return lastValueFrom(
      this.httpClient
        .request<{ token: string }>({
          method: 'POST',
          url: '/login',
          body: {
            email,
            password,
          },
        })
        .pipe(
          tap((response) => {
            if (response.isErr()) return;
            this.storage.set<string>('authToken', response.unwrap().token);
            this._isLoggedIn.set(true);
          })
        )
    );
  }

  async register(data: RegisterUserDto): Promise<Result<any, any>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/register',
        body: {
          ...data,
        },
      })
    );
  }

  async logout(): Promise<Result<any, any>> {
    return lastValueFrom(
      this.httpClient.request<void>({ method: 'POST', url: '/logout' }).pipe(
        tap(() => {
          this.clearAuthToken();
          this.clearUserProfile();
        })
      )
    );
  }

  async verifyEmail(code: string): Promise<Result<any, any>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/email/verify',
        body: {
          code,
        },
      })
    );
  }

  async verifyEmailResend(): Promise<Result<any, any>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/email/verify/resend',
      })
    );
  }

  async getProfile(): Promise<Result<UserModel, Error>> {
    const response = await lastValueFrom(
      this.httpClient.request<{
        data: { user: UserModel; legal_user?: any };
      }>({
        method: 'GET',
        url: '/profile',
      })
    );

    if (response.isErr()) return response;

    const { user } = response.unwrap()?.data ?? {};
    if (!user) {
      return new Result<UserModel, Error>(
        null,
        new Error('User data not found')
      );
    }
    this._userProfile.set(user);
    return new Result<UserModel, Error>(user, null);
  }

  async getPublicUserProfile(
    userId: string
  ): Promise<Result<PublicUserModel, Error>> {
    const response = await lastValueFrom(
      this.httpClient.request<{ user: PublicUserModel }>({
        method: 'GET',
        url: `/profile/public/${userId}`,
      })
    );

    if (response.isErr()) return response;

    const { user } = response.unwrap() || {};
    return new Result<PublicUserModel, Error>(user, null);
  }

  async updateProfile(
    data: UpdateUserDto
  ): Promise<Result<{ user: UserModel }, Error>> {
    const response = await lastValueFrom(
      this.httpClient.request<{ user: UserModel }>({
        method: 'PUT',
        url: '/profile/update',
        body: data,
      })
    );

    if (response.isOk()) {
      await this.getProfile();
    }
    return response;
  }

  async deleteProfile(): Promise<Result<DeleteUserResponseModel, Error>> {
    const response = await lastValueFrom(
      this.httpClient
        .request<void>({
          method: 'DELETE',
          url: '/profile/delete',
        })
        .pipe(
          tap(() => {
            this.clearAuthToken();
            this.clearUserProfile();
          })
        )
    );
    return new Result<DeleteUserResponseModel, Error>(
      { status: true, message: 'Профилът бе успешно изтрит' },
      null
    );
  }

  async changePassword(
    data: ChangePasswordDto
  ): Promise<Result<undefined, Error>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/profile/change-password',
        body: { ...data },
      })
    );
  }

  async forgotPassword(email: string): Promise<Result<undefined, Error>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/forgot-password',
        body: { email },
      })
    );
  }

  async resetPassword(
    resetPasswordData: ResetPasswordDto
  ): Promise<Result<undefined, Error>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/reset-password',
        body: { ...resetPasswordData },
      })
    );
  }

  updateProfileImage(
    profileImage: File
  ): Promise<Result<{ data: { profile_image_url: string } }, Error>> {
    const formData = objectToFormData({ image: profileImage });
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'POST',
        url: '/profile/image',
        body: formData,
      })
    );
  }

  deleteProfileImage(): Promise<Result<undefined, Error>> {
    return lastValueFrom(
      this.httpClient.request<any>({
        method: 'DELETE',
        url: '/profile/image',
      })
    );
  }
}
